/**
 * Humanizes vif filters into the strings the PDF renderer paints.
 *
 * This file is bundled into the PDF web worker (see `worker/report.worker.ts`).
 * Workers have no `window`, so do NOT import `common/i18n`, feature-flag
 * helpers (`VifSelectors`, `@socrata/platform/feature-flags`), or anything
 * that reads `window` at module load. Any flag or translated label that the
 * worker needs must be threaded in as data through `postMessage` /
 * `extraOptions` and passed down to these helpers as function arguments.
 */
import get from 'lodash/get';
import moment from 'moment';
import pluralize from 'common/js_utils/pluralize';

import { RELATIVE_FILTERS, SINGLE_SELECT_BY, PDF_FILTER_DATE_FORMAT } from 'common/dates';
import {
  type SoqlFilter,
  type BinaryOperator,
  FILTER_FUNCTION,
  type FilterArgument,
  FilterDataType,
  type FilterValue,
  type RelativeDateFilter,
  type TimeRangeFilter,
} from 'common/components/FilterBar/SoqlFilter';
import {
  getRelativeDateRange,
  getRelativeDateRangeTextDisplay,
} from 'common/visualizations/helpers/SoqlHelpers';
import { type Filter } from 'common/components/FilterBar/types';
import { numberFilterToHumanReadableParts } from './filterProcessors/numberFilterFormatter';
import { dateFilterToHumanReadableParts, isDateFilter } from './filterProcessors/dateFilterFormatter';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import { generateTextFilterDetails } from './filterProcessors/textFilterFormatter';

export type FilterWording = 'legacy' | 'editor';

const getOperator = (argument: any) => {
  switch (argument.operator) {
    case 'contains':
      return argument.operator;
    case 'does_not_contain':
    case 'starts_with':
    case 'ends_with':
      return argument.operator.replaceAll('_', ' ');
    default:
      return FILTER_FUNCTION_TEXT_CONFIG[argument.operator];
  }
};

export function getColumnFieldName(filter: SoqlFilter) {
  return filter.columns[0].fieldName;
}

function filterArgumentRequiresOperand(filterArgument: any) {
  return filterArgument.operator !== 'IS NULL' && filterArgument.operator !== 'IS NOT NULL';
}

/**
 * pulls the operand off a filter argument, returning an empty string for
 * IS NULL / IS NOT NULL where the operator carries the full meaning and
 * there is no operand to render.
 *
 * @param argument - the filter argument to inspect
 * @returns the argument's operand, or '' when the operator needs no operand
 */
export const getOperand = (argument: FilterArgument) => {
  if (!filterArgumentRequiresOperand(argument)) {
    return '';
  }

  return argument.operand;
};

/**
 * renders a BINARY_OPERATOR filter as the single-line human-readable string
 * the pdf surfaces in the filters section. when `matchEditorWording`
 * is true and the filter is a checkbox, emits the in-grid editor wording
 * (True / False / (No value), joined with ', ', sorted into
 * True -> False -> (No value)). otherwise falls back to the legacy ' or '
 * join with capitalized operand and 'blank' for IS NULL.
 *
 * @param filter - the binary-operator filter to render
 * @param columnName - the display name to prefix the predicate with
 * @param dataType - the filter's data type, used to detect the checkbox path
 * @param wording - 'editor' for new checkbox wording that matches the filter editor,
 *   'legacy' for the original wording format
 * @returns the rendered filter string, e.g. 'present is True, (No value)'
 */
export const binaryOperatorFilterToText = (
  filter: BinaryOperator,
  columnName: string,
  dataType: FilterDataType | undefined,
  wording: FilterWording = 'legacy',
): string => {
  if (isArray(filter.arguments)) {
    const isCheckbox = dataType === FilterDataType.CHECKBOX;
    const editorMode = isCheckbox && wording === 'editor';
    const conjunction = editorMode ? ', ' : ' or ';
    // editor mode renders the boolean values directly after the column name
    // ('present True, (No value)') — the 'is' prefix used by the legacy
    // wording is dropped because the values themselves carry the meaning.
    // legacy still derives the prefix from the first argument, which lands
    // on 'is blank' when IS NULL is first.
    const firstOperator = editorMode ? '' : getOperator(filter.arguments[0]);

    const mappedValues = map(filter.arguments, (argument: FilterArgument) => {
      const currentOperator = getOperator(argument);
      const containsBlankCondition =
        currentOperator === FILTER_FUNCTION_TEXT_CONFIG['IS NULL'] ||
        currentOperator === FILTER_FUNCTION_TEXT_CONFIG['IS NOT NULL'];

      // in editor mode every IS NULL / IS NOT NULL renders as '(No value)'
      // regardless of argument order, so picking (No value) first in the GFB
      // produces the same output as picking it last.
      if (editorMode && containsBlankCondition) {
        return '(No value)';
      }

      if (containsBlankCondition && firstOperator !== currentOperator) {
        return 'blank';
      }

      if (isCheckbox) {
        // legacy: when the only argument is IS NULL/IS NOT NULL there's no
        // operand and the operator carries the meaning, so leave the operand
        // slot empty. editor mode already short-circuits the IS NULL path
        // above, so this only runs in legacy.
        if (!filterArgumentRequiresOperand(argument)) {
          return '';
        }

        if (editorMode) {
          return checkboxOperandLabel(getOperand(argument));
        }

        // legacy path: capitalize the first letter of the raw operand string
        const stringOperand = String(getOperand(argument));
        return stringOperand.charAt(0).toUpperCase() + stringOperand.slice(1);
      }

      return getOperand(argument);
    });

    if (editorMode) {
      sortCheckboxValues(mappedValues);
    }

    const values = mappedValues.join(conjunction);
    // If we're going to use the new UI
    if (wording === 'editor') {
      return replaceDoubleSpaces(`${columnName} ${values}`);
    }

    return replaceDoubleSpaces(`${columnName} ${firstOperator} ${values}`);
  } else {
    if (filterArgumentRequiresOperand(filter.arguments)) {
      return '';
    }

    const operator = getOperator(filter.arguments);
    const encodedOperand = getOperand(filter.arguments);

    return `${columnName} is ${operator} ${encodedOperand}`;
  }
};

function replaceDoubleSpaces(text: string) {
  return text.replace('  ', ' ');
}

function binaryComputedGeoregionOperatorToText(filter: any) {
  const operator = getOperator(filter.arguments);
  const computedColumnName = filter.arguments.computedColumnName;
  const operand = getOperand(filter.arguments.operand);
  return `${computedColumnName}: ${operator} ${operand}`;
}

function processTimeRangeFilter(filter: any) {
  const singleSelectBy = get(filter, 'singleSelect.by');
  const argumentsStart = get(filter, 'arguments.start');
  const argumentsEnd = get(filter, 'arguments.end');

  let isRelativeStart = false;
  let startMoment;
  let endMoment;

  if (argumentsStart === RELATIVE_FILTERS.TODAY) {
    startMoment = moment().startOf('day');
    isRelativeStart = true;
  } else if (argumentsStart === RELATIVE_FILTERS.YESTERDAY) {
    startMoment = moment().startOf('day').subtract(1, 'days');
    isRelativeStart = true;
  } else {
    startMoment = moment(argumentsStart);
  }

  if (argumentsEnd === RELATIVE_FILTERS.TODAY) {
    endMoment = moment();
  } else if (argumentsEnd === RELATIVE_FILTERS.YESTERDAY) {
    // Set end to the start of today so all of yesterday is included
    endMoment = moment().startOf('day');
  } else {
    endMoment = moment(argumentsEnd);
  }

  // If we have a relative start argument of 'today' or 'yesterday' and is it
  // single select by day, adjust the end date appropriately.
  if (isRelativeStart && singleSelectBy === SINGLE_SELECT_BY.DAY) {
    endMoment = startMoment.clone().add(1, 'days');
  }

  const start = startMoment.format(PDF_FILTER_DATE_FORMAT);
  const end = endMoment.format(PDF_FILTER_DATE_FORMAT);

  return { start, end };
}

function timeRangeFilterToTextLegacy(filter: TimeRangeFilter, columnName: string) {
  const { start, end } = processTimeRangeFilter(filter);
  if (filter.arguments.type === RELATIVE_FILTERS.DATE_TO_TODAY) {
    return `${columnName} ${start} to today (${end})`;
  }
  return `${columnName} from ${start} to ${end}`;
}

function timeRangeFilterToTextEditor(filter: TimeRangeFilter, columnName: string) {
  const { start, end } = processTimeRangeFilter(filter);
  if (filter.arguments.type === RELATIVE_FILTERS.DATE_TO_TODAY) {
    return `${columnName} From ${start} to Today (${end})`;
  }
  return `${columnName} From ${start} to ${end}`;
}

function timeRangeFilterToText(
  filter: TimeRangeFilter,
  columnName: string,
  wording: FilterWording = 'legacy',
) {
  return wording === 'editor'
    ? timeRangeFilterToTextEditor(filter, columnName)
    : timeRangeFilterToTextLegacy(filter, columnName);
}

function dateFilterDisplayFormatLegacy(startDate: string, endDate: string, filter: any, columnName: string) {
  const startMoment = moment(startDate).format(PDF_FILTER_DATE_FORMAT);
  const endMoment = moment(endDate).format(PDF_FILTER_DATE_FORMAT);
  const { type, value, period } = filter.arguments;
  let result = '';

  switch (type) {
    case RELATIVE_FILTERS.TODAY:
      result = `${columnName} today (${startMoment})`;
      break;
    case RELATIVE_FILTERS.YESTERDAY:
      result = `${columnName} yesterday (${startMoment})`;
      break;
    case RELATIVE_FILTERS.THIS_WEEK:
      result = `${columnName} this week (${startMoment} - ${endMoment})`;
      break;
    case RELATIVE_FILTERS.THIS_MONTH:
      result = `${columnName} this month (${startMoment} - ${endMoment})`;
      break;
    case RELATIVE_FILTERS.THIS_QUARTER:
      result = `${columnName} this quarter (${startMoment} - ${endMoment})`;
      break;
    case RELATIVE_FILTERS.THIS_YEAR:
      result = `${columnName} this year (${startMoment} - ${endMoment})`;
      break;
    case RELATIVE_FILTERS.CUSTOM: {
      const periodFormat = value > 1 ? pluralize(period) : period;
      result = `last ${value} ${periodFormat} from ${endMoment}`;
      break;
    }
    default:
      result = `${columnName} from ${startDate} to ${endDate}`;
      break;
  }
  return result;
}

function relativeDateRangeFilterToText(filter: any, columnName: string, wording: FilterWording = 'legacy') {
  /**
   * `getRelativeDateRange` can return `undefined`. Can't destructure if the value can be undefined.
   * Looks like `getRelativeDateRange` doesn't handle every case and probably could return something
   * by default.
   */
  const { startDate, endDate } = getRelativeDateRange(filter)!;
  if (wording === 'editor') {
    return dateFilterDisplayFormat(startDate, endDate, filter, columnName);
  }

  return dateFilterDisplayFormatLegacy(startDate, endDate, filter, columnName);
}

function includeOrExcludeDataRangeFilterToText(filter: any, columnName: string) {
  const inclusiveString = filter.function === FILTER_FUNCTION.RANGE_INCLUSIVE ? '(inclusive)' : '';

  const includeNullValuesText = get(filter, 'arguments.includeNullValues', true)
    ? 'and includes blank values'
    : '';

  const start = filter.arguments.start;
  const end = filter.arguments.end;
  return replaceDoubleSpaces(
    `${columnName} is between ${start} and ${end} ${inclusiveString} ${includeNullValuesText}`.trim(),
  );
}
const FILTER_FUNCTION_TEXT_CONFIG: any = {
  '=': 'is',
  '!=': 'is not',
  '<': 'is less than',
  '<=': 'is at most',
  '>': 'is greater than',
  '>=': 'is at least',
  'IS NULL': 'is blank',
  'IS NOT NULL': 'is not blank',
};

function comparisonFilterToText(filter: any, columnName: string) {
  const includeNullValuesText = get(filter, 'arguments.includeNullValues', true)
    ? 'and includes blank values'
    : '';

  const value = filter.arguments.value;
  return `${columnName} ${FILTER_FUNCTION_TEXT_CONFIG[filter.function]} ${value} ${includeNullValuesText}`.trim();
}

function isNotFilterToText(filter: any, columnName: string) {
  const notInFilterValues = filter.arguments.join(', ');
  return `${columnName} is not ${notInFilterValues}`;
}

export function filterToHumanReadableString(filter: Filter, wording: FilterWording = 'legacy'): string {
  if (FILTER_FUNCTION.NOOP === filter.function || FILTER_FUNCTION.WITHIN_CIRCLE === filter.function) {
    return '';
  }

  const dataType = filter.dataTypeName;
  const humanReadable = filter.columns
    .map((column: any) => {
      const columnName = column.displayName || column.fieldName;

      switch (filter.function) {
        case FILTER_FUNCTION.BINARY_OPERATOR:
          return binaryOperatorFilterToText(filter, columnName, dataType, wording);
        case FILTER_FUNCTION.BINARY_OPERATOR_COMPUTED_GEOREGION:
          return binaryComputedGeoregionOperatorToText(filter);
        case FILTER_FUNCTION.TIME_RANGE:
          return timeRangeFilterToText(filter, columnName, wording);
        case FILTER_FUNCTION.RELATIVE_DATE_RANGE:
          return relativeDateRangeFilterToText(filter, columnName, wording);
        case FILTER_FUNCTION.VALUE_RANGE:
        case FILTER_FUNCTION.RANGE_INCLUSIVE:
        case FILTER_FUNCTION.RANGE_EXCLUSIVE:
          return includeOrExcludeDataRangeFilterToText(filter, columnName);
        case FILTER_FUNCTION.EXCLUDE_NULL:
          return `${columnName} is not blank`;
        case FILTER_FUNCTION.EQUALS:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.NOT_EQUAL:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.LESS_THAN:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.LESS_THAN_EQUAL_TO:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.GREATER_THAN:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.GREATER_THAN_EQUAL_TO:
          return comparisonFilterToText(filter, columnName);
        case FILTER_FUNCTION.IN:
          const inFilterValues = filter.arguments.join(', ');
          return `${columnName} is ${inFilterValues}`;
        case FILTER_FUNCTION.NOT_IN:
          return isNotFilterToText(filter, columnName);
        // @ts-ignore
        case 'not in':
          return isNotFilterToText(filter, columnName);
        default:
          return '';
      }
    })
    .join(', ');

  return humanReadable;
}

// Used under enable_table_printing_improvement_for_filters feature flag

function reportingPeriodTimeRangeFilterToText(filter: TimeRangeFilter) {
  const { start, end } = processTimeRangeFilter(filter);
  return `${start} to ${end}`;
}

function reportingPeriodRelativeDateRangeFilterToText(filter: RelativeDateFilter) {
  const dateRange = getRelativeDateRangeTextDisplay(filter);
  if (!dateRange) {
    return '';
  }

  const startMomentFormatted = moment(dateRange.startDate).format(PDF_FILTER_DATE_FORMAT);
  // For single-date filters (TODAY, YESTERDAY), endDate is empty
  if (!dateRange.endDate) {
    return startMomentFormatted;
  }

  const endMomentFormatted = moment(dateRange.endDate).format(PDF_FILTER_DATE_FORMAT);
  return `${startMomentFormatted} to ${endMomentFormatted}`;
}

export function getReportingPeriodDisplayText(filter: TimeRangeFilter | RelativeDateFilter) {
  return filter.function === FILTER_FUNCTION.RELATIVE_DATE_RANGE
    ? reportingPeriodRelativeDateRangeFilterToText(filter)
    : reportingPeriodTimeRangeFilterToText(filter);
}

/**
 * Formats date filter display text for the new PDF UI.
 * Converts date ranges to human-readable format with "to" separator.
 *
 * Note: The endDate parameter comes from getRelativeDateRange() which returns exclusive end dates.
 * For display purposes, we subtract 1 day to show inclusive end dates (what users expect to see).
 *
 * @param startDate - ISO date string from getRelativeDateRange()
 * @param endDate - ISO date string from getRelativeDateRange() (exclusive, needs -1 day for display)
 * @param filter - Relative date filter configuration
 * @param columnName - Display name of the filtered column
 * @returns Human-readable filter description
 */
export function dateFilterDisplayFormat(
  startDate: string,
  endDate: string,
  filter: RelativeDateFilter,
  columnName: string,
) {
  const startMoment = moment(startDate).format(PDF_FILTER_DATE_FORMAT);
  const endMoment = moment(endDate).format(PDF_FILTER_DATE_FORMAT);
  // Subtract 1 day to display inclusive end date for range filters
  const endMomentForViews = moment(endDate).subtract(1, 'days').format(PDF_FILTER_DATE_FORMAT);

  const { type, value, period } = filter.arguments;

  switch (type) {
    case RELATIVE_FILTERS.TODAY:
      return `${columnName} Today (${startMoment})`;
    case RELATIVE_FILTERS.YESTERDAY:
      return `${columnName} Yesterday (${startMoment})`;
    case RELATIVE_FILTERS.THIS_WEEK:
      return `${columnName} This Week (${startMoment} to ${endMomentForViews})`;
    case RELATIVE_FILTERS.THIS_MONTH:
      return `${columnName} This Month (${startMoment} to ${endMomentForViews})`;
    case RELATIVE_FILTERS.THIS_QUARTER:
      return `${columnName} This Quarter (${startMoment} to ${endMomentForViews})`;
    case RELATIVE_FILTERS.THIS_YEAR:
      return `${columnName} This Year (${startMoment} to ${endMomentForViews})`;
    case RELATIVE_FILTERS.CUSTOM: {
      let numericValue = 1;
      if (typeof value === 'number') {
        numericValue = value;
      } else if (typeof value === 'string') {
        numericValue = parseInt(value, 10);
      }
      const periodFormat = numericValue > 1 ? pluralize(period ?? '') : (period ?? '');
      return `${columnName} Last ${value} ${periodFormat} (${startMoment} to ${endMomentForViews})`;
    }
    default:
      return `${columnName} from ${startMoment} to ${endMoment}`;
  }
}

/**
 * weight used to order checkbox filter values for display: 1 for True, 2 for
 * False, 3 for anything else (so 'blank' and '(No value)' fall to the end).
 * comparison is case-insensitive and accepts both boolean primitives and their
 * string forms.
 *
 * @param value - the checkbox value to weigh
 * @returns 1 for True, 2 for False, 3 for everything else
 * @example
 * getCheckboxSortWeight(true)         // => 1
 * getCheckboxSortWeight('FALSE')      // => 2
 * getCheckboxSortWeight('(No value)') // => 3
 */
export const getCheckboxSortWeight = (value: FilterValue): number => {
  const stringVal = `${value}`.toLowerCase();

  if (stringVal === 'true') {
    return 1;
  }
  if (stringVal === 'false') {
    return 2;
  }

  // for '(No value)' or 'blank'
  return 3;
};

/**
 * sorts checkbox filter values into the printed order: True, then False, then
 * (No value). anything that isn't true/false falls into the last bucket, so
 * 'blank' and '(No value)' both sort to the end. sorts in place and returns
 * the same array for composability.
 *
 * @param values - the values to sort, mutated in place
 * @returns the input array, sorted
 * @example
 * sortCheckboxValues(['False', '(No value)', 'True'])
 * // => ['True', 'False', '(No value)']
 */
export const sortCheckboxValues = (values: FilterValue[]): FilterValue[] => {
  return values.sort((a, b) => getCheckboxSortWeight(a) - getCheckboxSortWeight(b));
};

/**
 * maps a checkbox filter operand to the wording shown in the QFB/GFB editor:
 * boolean true or string 'true' becomes 'True', boolean false or string 'false'
 * becomes 'False', and anything else (null, undefined, '', other strings)
 * falls back to '(No value)'.
 *
 * @param operand - the raw filter operand
 * @returns the editor-style label for the operand
 */
export const checkboxOperandLabel = (operand: FilterValue): string => {
  if (operand === true || operand === 'true') {
    return 'True';
  }
  if (operand === false || operand === 'false') {
    return 'False';
  }
  return '(No value)';
};

export interface FilterHumanReadableParts {
  /**
   * column-name prefix to render in bold. empty string when the filter type
   * does not call for a bolded column name. includes the trailing space when
   * non-empty so the renderer can place the predicate run flush with it.
   */
  columnName: string;
  /**
   * remaining text to render in normal weight. for non-bolded filter types
   * this contains the full human-readable string.
   */
  predicate: string;
}

/**
 * Produces a structured pair so the renderer can emit the column name in bold
 * and the predicate in normal weight. Boolean, number and date filters get styled
 * formatting; all other types return an empty `columnName` and fall back to
 * a single normal-weight run.
 *
 * Runs only if enable_table_printing_improvement_for_filters is set to true
 *
 * @param filter - the filter to render
 * @returns {FilterHumanReadableParts} Object with:
 *   - columnName: Text to render in bold (includes trailing space/colon), empty string if no styling needed
 *   - predicate: Text to render in normal weight (the filter condition/value)
 */
export const filterToHumanReadableParts = (filter: Filter): FilterHumanReadableParts => {
  const isNumberFilter = filter.dataTypeName === FilterDataType.NUMBER;

  // Handle number filters with structured parts
  if (isNumberFilter) {
    const numberParts = numberFilterToHumanReadableParts(filter);
    if (numberParts) {
      return {
        columnName: `${numberParts.columnName} `,
        predicate: numberParts.predicate,
      };
    }
  }

  if (filter.function === FILTER_FUNCTION.BINARY_OPERATOR) {
    // Handle text filters with structured parts
    if (filter.dataTypeName === FilterDataType.TEXT) {
      const { columnName, predicate } = generateTextFilterDetails(filter);
      return {
        columnName: `${columnName} `,
        predicate,
      };
    }

    // Handle boolean binary operators with styled column names
    if (filter.dataTypeName === FilterDataType.CHECKBOX && filter.columns.length) {
      const column = filter.columns[0];
      const displayName = column.displayName || column.fieldName;
      const fullText = binaryOperatorFilterToText(filter, displayName, FilterDataType.CHECKBOX, 'editor');
      const prefix = `${displayName} `;

      if (fullText.startsWith(prefix)) {
        return {
          columnName: prefix,
          predicate: fullText.slice(prefix.length),
        };
      }
    }
  }

  // Handle date filters with structured parts
  if (isDateFilter(filter)) {
    const dateParts = dateFilterToHumanReadableParts(filter);
    if (dateParts) {
      return {
        columnName: `${dateParts.columnName} `,
        predicate: dateParts.predicate,
      };
    }
  }

  // Fallback for all other filter types - no styled column name
  return {
    columnName: '',
    predicate: filterToHumanReadableString(filter, 'editor'),
  };
};
